import { useCallback, useState } from 'react';

import { ShopItemForStoreData } from '@shared/api/shopItem/shopItem.interface';
import {
  deleteShopOrderById,
  sendVBankAlimUser,
  verifyShopOrderById,
} from '@shared/api/shopOrder/shopOrder.controller';
import { ShopOrder } from '@shared/api/shopOrder/shopOrder.interface';
import { User } from '@shared/api/user/user.interface';
import { toastError } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

import { IShopItemFormData } from '../state/shopItemCheckout.interface';
import { getBootpayRequestPaymentDto } from '../utils/getBootpayRequestDto';
declare global {
  interface Window {
    Bootpay: any;
  }
}

export interface BootpayRequestParams {
  shopOrder: ShopOrder;
  shopItem: ShopItemForStoreData;
  userProfile: User<string>;
  formData: IShopItemFormData;
  bugoId: string | null;
}

/**
 * 부트페이 결제 훅 (토스페이먼츠 폴백용)
 *
 * 기존 ShopItemCheckoutPage의 부트페이 결제 로직을 훅으로 추출.
 * Bootpay.requestPayment() → done/issued/cancel/error 이벤트 처리.
 */
export const useBootpayCheckout = () => {
  const navigate = useNavigate();
  const { storeRole } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestPayment = useCallback(
    async (params: BootpayRequestParams) => {
      if (!window.Bootpay) {
        toastError('결제 모듈을 불러올 수 없습니다.');
        return;
      }

      setIsSubmitting(true);
      try {
        const paymentResponse = await window.Bootpay.requestPayment(
          getBootpayRequestPaymentDto({
            formData: params.formData,
            shopOrder: params.shopOrder,
            shopItem: params.shopItem,
            userProfile: params.userProfile,
          }),
        );

        switch (paymentResponse.event) {
          case 'done':
            await verifyShopOrderById(params.shopOrder._id, storeRole());
            navigate(
              `/flower-store/order/${params.shopOrder._id}/payment/?success=true&bugoId=${params.bugoId}`,
            );
            break;
          case 'issued': {
            const { vbank_data } = paymentResponse.data;
            const vBank = `${vbank_data.bank_name}\n${vbank_data.bank_account}`;
            await sendVBankAlimUser(params.shopOrder._id, vBank);
            navigate(
              `/flower-store/order/${params.shopOrder._id}/payment/?success=true&bugoId=${params.bugoId}`,
            );
            break;
          }
        }
      } catch (error: any) {
        switch (error.event) {
          case 'cancel':
            await deleteShopOrderById(params.shopOrder._id, storeRole()).catch(
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              () => {},
            );
            break;
          case 'error':
            toastError(`결제 실패\n${error.message || ''}`);
            break;
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, storeRole],
  );

  return { requestPayment, isSubmitting };
};
